import { Trans, useLingui } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { PatientState, PermissionBit, Sex } from "../../../api";
import { ApiConnector, RequestError } from "../../../api/api-connector";
import { AppModel } from "../../../app/app-model";
import { useCountries } from "../../../catalogs/countries";
import { Button } from "../../../components/ui-kit/button";
import { useDialog } from "../../../components/ui-kit/dialog-provider";
import { Combobox } from "../../../components/ui-kit/fields/combobox";
import { DateInput } from "../../../components/ui-kit/fields/date-input";
import { NumberInput } from "../../../components/ui-kit/fields/number-input";
import { Radio } from "../../../components/ui-kit/fields/radio";
import { Select } from "../../../components/ui-kit/fields/select";
import { TextInput } from "../../../components/ui-kit/fields/text-input";
import { resolveEnumLabel } from "../../../components/ui-kit/fields/utils";
import { FormRow } from "../../../components/ui-kit/form/form-row";
import { FormRowEditable } from "../../../components/ui-kit/form/form-row-editable";
import { FormTable } from "../../../components/ui-kit/form/form-table";
import { LazyRichTextEditor } from "../../../components/ui-kit/lazy-rich-text-editor";
import { RichTextContent } from "../../../components/ui-kit/rich-text-content";
import {
  callStatuses,
  informedConsent,
  monitoringTypes,
  patientStates,
  patientStatuses,
  raceOptions,
  reportStatuses,
  watchKinds,
  watchStatuses,
} from "../../../constants/enum-to-text";
import {
  hasAdminAccess,
  hasMedicalAccess,
  hasMonitoringAccess,
  hasPermission,
} from "../../../constants/permissions";
import { physicianName } from "../../../utils/physician";
import { floatRounding } from "../../../utils/utils";
import { CountrySelect } from "../country-select";
import { CreateUserForm } from "../create-user-form";
import { MonitoringMuteFormRow } from "../monitoring-mute-form-row";
import { NameEditDialog } from "../name-edit-dialog";
import { Patient } from "../patient";
import { PatientFeatureFlags } from "../patient-feature-flags";
import { PatientMessageFormRow } from "../patient-message-form-row";
import { RegionSelect } from "../region-select";
import { ScheduleFormRow } from "../schedule-form-row";
import { SelfMeasurementConfig } from "../self-measurement-config";
import { UserManagement } from "../user-management";

export const PatientSettings = observer(function PatientSettings(props: {
  patient: Patient;
  app: AppModel;
  api: ApiConnector;
}) {
  const { t } = useLingui();
  const patient = props.patient;
  const data = patient?.data;
  const countries = useCountries();
  const form = patient?.detailsForm;
  const updateField = (
    editedField: Record<string, any>,
    sanitize: boolean = true,
  ) => {
    void patient.updatePatientField(editedField, sanitize);
  };
  const { confirm, alert } = useDialog();
  const navigate = useNavigate();
  const [addingConnUser, setAddingConnUser] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [deleteExpanded, setDeleteExpanded] = useState(false);

  const handleDeletePatient = async () => {
    const confirmed = await confirm(
      t`Are you sure you want to delete patient "${data?.full_name}" (ID: ${data?.patient_id})? This action cannot be undone.`,
      t`Delete patient`,
      { destructive: true },
    );
    if (!confirmed) return;
    try {
      await patient.deletePatient();
      await alert(t`Patient was deleted successfully.`);
      void navigate("/");
    } catch (e) {
      if (e instanceof RequestError) {
        if (e.response.status === 423) {
          await alert(
            t`This patient has existing data and cannot be deleted.`,
            t`Cannot delete patient`,
          );
        } else {
          await alert(
            t`Failed to delete patient: ${e.response.status} error.`,
            t`Error`,
          );
        }
      } else {
        await alert(
          t`An unexpected error occurred while deleting the patient.`,
          t`Error`,
        );
      }
    }
  };

  if (!data) {
    return null;
  }

  return (
    <div>
      <NameEditDialog
        open={editingName}
        onOpenChange={setEditingName}
        data={data}
        onSave={(newName) => updateField(newName, false)}
      />

      {/* Two columns only from lg — at md the label / value / Edit rows are
          too cramped in a half-width card. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
        {/* Card 1: Personal info */}
        <div className="rounded-md border p-4">
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
            <Trans>Personal information</Trans>
          </div>
          <FormTable big>
            <FormRowEditable
              label={t`Name`}
              viewMode={data.full_name}
              editable={hasAdminAccess(props.api.permissions)}
              onEditClick={() => setEditingName(true)}
            />

            <FormRowEditable
              form={form}
              attr="country"
              label={t`Country`}
              model={data}
              viewMode={data.country}
              editMode={() => (
                <CountrySelect
                  attr="country"
                  regionAttr="region"
                  model={form.editedField}
                />
              )}
              onChange={updateField}
            />

            <FormRowEditable
              form={form}
              attr="region"
              label={t`Region`}
              model={data}
              viewMode={
                data.region !== undefined
                  ? countries.getRegionSchema(data.country, data.region)
                      ?.region_name
                  : undefined
              }
              editMode={() => (
                <RegionSelect
                  attr="region"
                  model={form.editedField}
                  country={data.country}
                  required
                />
              )}
              onChange={updateField}
            />

            <FormRowEditable
              form={form}
              attr="date_of_birth"
              label={t`Date of birth`}
              model={data}
              viewMode={
                data.date_of_birth != null
                  ? new Date(data.date_of_birth).toLocaleDateString("sk-SK")
                  : undefined
              }
              editMode={() => (
                <DateInput
                  attr="date_of_birth"
                  model={form.editedField}
                  type="date"
                />
              )}
              onChange={updateField}
            />

            <FormRowEditable
              form={form}
              attr="sex"
              label={t`Sex`}
              model={data}
              viewMode={data.sex}
              editMode={() => (
                <Radio attr="sex" model={form.editedField} values={Sex} />
              )}
              onChange={updateField}
            />

            <FormRowEditable
              form={form}
              attr="race"
              label={t`Race`}
              model={data}
              viewMode={data.race}
              editMode={() => (
                <Combobox
                  attr="race"
                  model={form.editedField}
                  options={raceOptions}
                  required
                />
              )}
              onChange={updateField}
            />

            <FormRowEditable
              form={form}
              attr="height"
              label={t`Height [cm]`}
              model={data}
              viewMode={floatRounding(data.height, 1)}
              editMode={() => (
                <NumberInput
                  attr="height"
                  model={form.editedField}
                  min={0}
                  max={300}
                  step={1}
                />
              )}
              onChange={(val) => updateField(val, false)}
            />

            <FormRowEditable
              form={form}
              attr="description"
              label={t`Description`}
              model={data}
              viewMode={
                <div className="max-h-50 overflow-y-auto">
                  {data.description}
                </div>
              }
              editMode={() => (
                <TextInput
                  attr="description"
                  model={form.editedField}
                  multiline="autosize"
                  width={200}
                />
              )}
              onChange={(val) => updateField(val, false)}
            />

            {/* Loaded only with medical access. */}
            {patient.activeMessage && (
              <PatientMessageFormRow
                patient={patient}
                form={form}
                api={props.api}
              />
            )}
          </FormTable>
        </div>

        {/* Card 2: Monitoring */}
        <div className="rounded-md border p-4">
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
            <Trans>Monitoring</Trans>
          </div>
          <FormTable big>
            {/* Patient state row — manual 3-column layout to align Toggle button with Edit buttons */}
            {hasMonitoringAccess(props.api.permissions) && (
              <>
                <div className="font-bold flex items-center min-h-8">
                  <label>
                    <Trans>Patient state:</Trans>
                  </label>
                </div>
                <div className="flex items-center min-h-8 max-w-100 min-w-0">
                  <span
                    className={
                      data.patient_state === PatientState.high_risk
                        ? "text-danger-foreground font-medium"
                        : "text-success-foreground font-medium"
                    }
                  >
                    {resolveEnumLabel(
                      patientStates[data.patient_state as PatientState],
                    )}
                  </span>
                </div>
                <div className="flex items-center min-h-8">
                  <Button
                    size="sm"
                    onClick={async () => await patient.togglePatientState()}
                  >
                    <Trans>Toggle state</Trans>
                  </Button>
                </div>
              </>
            )}

            {hasMonitoringAccess(props.api.permissions) && (
              <>
                <FormRowEditable
                  form={form}
                  attr="monitoring_call_status"
                  label={t`Call status`}
                  model={data}
                  viewMode={
                    data.monitoring_call_status != null
                      ? resolveEnumLabel(
                          callStatuses[data.monitoring_call_status],
                        )
                      : undefined
                  }
                  editMode={() => (
                    <Select
                      attr="monitoring_call_status"
                      model={form.editedField}
                      values={callStatuses}
                    />
                  )}
                  onChange={updateField}
                />

                <FormRowEditable
                  form={form}
                  attr="monitoring_report_status"
                  label={t`Report status`}
                  model={data}
                  viewMode={
                    data.monitoring_report_status != null
                      ? resolveEnumLabel(
                          reportStatuses[data.monitoring_report_status],
                        )
                      : undefined
                  }
                  editMode={() => (
                    <Select
                      attr="monitoring_report_status"
                      model={form.editedField}
                      values={reportStatuses}
                    />
                  )}
                  onChange={updateField}
                />

                <FormRowEditable
                  form={form}
                  attr="monitoring_watch_kind"
                  label={t`Monitoring room`}
                  model={data}
                  viewMode={
                    data.monitoring_watch_kind != null
                      ? resolveEnumLabel(watchKinds[data.monitoring_watch_kind])
                      : undefined
                  }
                  editMode={() => (
                    <Select
                      attr="monitoring_watch_kind"
                      model={form.editedField}
                      values={watchKinds}
                    />
                  )}
                  onChange={updateField}
                />

                <FormRowEditable
                  form={form}
                  attr="monitoring_watch_status"
                  label={t`Watch status`}
                  model={data}
                  viewMode={
                    data.monitoring_watch_status != null
                      ? resolveEnumLabel(
                          watchStatuses[data.monitoring_watch_status],
                        )
                      : undefined
                  }
                  editMode={() => (
                    <Select
                      attr="monitoring_watch_status"
                      model={form.editedField}
                      values={watchStatuses}
                    />
                  )}
                  onChange={updateField}
                />

                <FormRowEditable
                  form={form}
                  attr="medical_monitoring_note"
                  label={t`Medical note`}
                  model={data}
                  viewMode={
                    <RichTextContent
                      className="prose prose-sm max-h-50 max-w-full overflow-y-auto"
                      value={data.medical_monitoring_note ?? ""}
                    />
                  }
                  editMode={(editedField) => (
                    <LazyRichTextEditor
                      className="w-full max-w-75"
                      toolbar={["text-formatting"]}
                      value={editedField.medical_monitoring_note ?? ""}
                      onChange={(v) => {
                        editedField.medical_monitoring_note = v;
                      }}
                    />
                  )}
                  onChange={(val) => updateField(val, false)}
                />

                <FormRowEditable
                  form={form}
                  attr="technical_monitoring_note"
                  label={t`Technical note`}
                  model={data}
                  viewMode={
                    <RichTextContent
                      className="prose prose-sm max-h-50 max-w-full overflow-y-auto"
                      value={data.technical_monitoring_note ?? ""}
                    />
                  }
                  editMode={(editedField) => (
                    <LazyRichTextEditor
                      className="w-full max-w-75"
                      toolbar={["text-formatting"]}
                      value={editedField.technical_monitoring_note ?? ""}
                      onChange={(v) => {
                        editedField.technical_monitoring_note = v;
                      }}
                    />
                  )}
                  onChange={(val) => updateField(val, false)}
                />
              </>
            )}

            <ScheduleFormRow patient={patient} />
          </FormTable>
        </div>

        {/* Card 3: Monitoring info and consents */}
        {hasMonitoringAccess(props.api.permissions) && (
          <div className="rounded-md border p-4">
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
              <Trans>Monitoring info and consents</Trans>
            </div>
            <FormTable big>
              <FormRow
                label={t`Added`}
                data={
                  data.created_at != null
                    ? new Date(data.created_at).toLocaleString("sk-SK")
                    : undefined
                }
              />

              <FormRow label={t`Added by`} data={data.user?.username} />

              <FormRow
                label={t`Informed consent`}
                data={
                  <div
                    className={
                      patient.consentOk()
                        ? "text-success-foreground"
                        : "text-danger-foreground"
                    }
                  >
                    {data.informed_consent !== undefined
                      ? resolveEnumLabel(informedConsent[data.informed_consent])
                      : undefined}
                  </div>
                }
              />

              {patient.isPaperConsent() && (
                <FormRow
                  label={t`Change consent`}
                  data={
                    <>
                      {!patient.consentOk() && (
                        <Button
                          onClick={async () => {
                            await props.api.approvePaperConsent(
                              data.patient_id,
                            );
                          }}
                        >
                          <Trans>Approve</Trans>
                        </Button>
                      )}
                      {patient.consentOk() && (
                        <Button
                          destructive
                          onClick={async () => {
                            await props.api.revokePaperConsent(data.patient_id);
                          }}
                        >
                          <Trans>Revoke</Trans>
                        </Button>
                      )}
                    </>
                  }
                />
              )}

              {!patient.isPaperConsent() &&
                hasPermission(
                  props.api.permissions,
                  PermissionBit.BYPASS_CONSENT,
                ) && (
                  <FormRow
                    label={t`Change consent`}
                    data={
                      <>
                        {!patient.consentOk() && (
                          <Button
                            onClick={async () => {
                              await props.api.approveTeleConsent(
                                data.patient_id,
                              );
                            }}
                          >
                            <Trans>Approve</Trans>
                          </Button>
                        )}
                        {patient.consentOk() && (
                          <Button
                            destructive
                            onClick={async () => {
                              await props.api.revokeTeleConsent(
                                data.patient_id,
                              );
                            }}
                          >
                            <Trans>Revoke</Trans>
                          </Button>
                        )}
                      </>
                    }
                  />
                )}

              <FormRowEditable
                form={form}
                attr="monitoring_type"
                label={t`Monitoring type`}
                model={data}
                viewMode={
                  data.monitoring_type != null
                    ? resolveEnumLabel(monitoringTypes[data.monitoring_type])
                    : undefined
                }
                editMode={() => (
                  <Select
                    attr="monitoring_type"
                    model={form.editedField}
                    values={monitoringTypes}
                  />
                )}
                onChange={updateField}
              />

              <FormRowEditable
                form={form}
                attr="patient_status"
                label={t`Patient status`}
                model={data}
                viewMode={
                  data.patient_status != null
                    ? resolveEnumLabel(patientStatuses[data.patient_status])
                    : undefined
                }
                editMode={() => (
                  <Select
                    attr="patient_status"
                    model={form.editedField}
                    values={patientStatuses}
                  />
                )}
                onChange={updateField}
              />

              <FormRow
                label={t`Connected to users`}
                data={(data.connected_users ?? [])
                  .map((user) => user.username)
                  .join("; ")}
              />

              <FormRow
                label={t`Is managed by physicians`}
                data={(data.physicians ?? []).map(physicianName).join("; ")}
              />

              <FormRow
                label={t`Is managed by users`}
                data={(data.managing_users ?? [])
                  .map((user) => user.username)
                  .join("; ")}
              />

              <FormRow
                label={t`Last measured PPG`}
                data={patient.heartCore?.last_ppg}
              />

              <FormRow label={t`Patient adherence`} data="N/A" />
            </FormTable>

            <Button
              onClick={() => setAddingConnUser(!addingConnUser)}
              className="mt-3"
            >
              <Trans>Create user for this patient</Trans>
            </Button>

            {addingConnUser && <CreateUserForm patient={patient} />}
          </div>
        )}

        {/* Card 4: Self-measurement config */}
        <div className="rounded-md border p-4">
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
            <Trans>Self-measurement config</Trans>
          </div>
          <SelfMeasurementConfig
            config={data.self_measurement_config ?? []}
            api={props.api}
            patient={props.patient}
            // Mirrors PUT /patients/{id}: medical routers + UPDATE_VISIBLE, no
            // monitoring needed (the BE scopes WHICH patients by role).
            editable={
              hasMedicalAccess(props.api.permissions) &&
              hasPermission(props.api.permissions, PermissionBit.UPDATE_VISIBLE)
            }
          />
        </div>

        {/* Card 5: User management (admin only) */}
        {hasAdminAccess(props.api.permissions) && (
          <div className="rounded-md border p-4">
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
              <Trans>User management</Trans>
            </div>
            <UserManagement patient={props.patient} api={props.api} />
          </div>
        )}

        {/* Card 6: Feature flags of the connected user. Gated on the carve-out
            of PUT /users/{uuid} - medical-update permission over the patient -
            which is what governs the write. The flags themselves ride the
            patient response, needing only MEDICAL_READ. */}
        {hasMedicalAccess(props.api.permissions) &&
          hasPermission(
            props.api.permissions,
            PermissionBit.UPDATE_VISIBLE,
          ) && (
            <div className="rounded-md border p-4">
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                <Trans>Feature flags</Trans>
              </div>
              <PatientFeatureFlags patient={props.patient} api={props.api} />
            </div>
          )}
      </div>

      {hasPermission(props.api.permissions, PermissionBit.VIEW_ALERTS) && (
        <div className="mt-5 mb-5">
          <h3 className="mt-9 mb-2 text-base font-bold">
            <Trans>Alerts</Trans>
          </h3>
          <div className="mb-3">
            <Trans>Mark all alerts of certain kinds as seen.</Trans>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={async () => {
                await patient.markMedicalAlertsSeen();
              }}
            >
              <Trans>Mark all medical as seen</Trans>
            </Button>
            <Button
              onClick={async () => {
                await patient.markTechAlertsSeen();
              }}
            >
              <Trans>Mark all technical / compliance as seen</Trans>
            </Button>
          </div>
          <div className="mt-3">
            <MonitoringMuteFormRow patient={patient} form={form} />
          </div>
        </div>
      )}

      {hasMonitoringAccess(props.api.permissions) && (
        <div className="mt-5 mb-5">
          <div className="flex flex-wrap gap-2 items-center mt-9 mb-2">
            <h3 className="text-base font-bold">
              <Trans>Delete patient</Trans>
            </h3>
            <button
              className="border-none text-xs"
              onClick={() => setDeleteExpanded(!deleteExpanded)}
            >
              {deleteExpanded ? "▲" : "▼"}
            </button>
          </div>
          {deleteExpanded && (
            <>
              <div className="mb-3">
                <Trans>
                  Permanently delete this patient and their connected user
                  account. Patients with existing data cannot be deleted.
                </Trans>
              </div>
              <Button destructive onClick={handleDeletePatient}>
                <Trans>Delete patient</Trans>
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
});
